<template>
  <el-dialog width="700px" :title="originator" :visible.sync="dialogVisible">
    <Loader v-if="loadingThreads" msg="Loading conversation"></Loader>
    <div class="thread-container" id="threadContainer">
      <div v-for="interaction in processedThreads" :key="interaction.id">
        <div class="message" :class="interaction.direction">
          <div class="audio-container" v-if="interaction.audio">
            <audio
              :src="interaction.audio"
              :ref="`audio${interaction.id}`"
              controls="controls"
              preload="none"
              @play="mediaStarted(interaction.id)"
              @ended="mediaEnded(interaction.id)"
              @pause="mediaEnded(interaction.id)"
            ></audio>
          </div>
          <div class="video-container" v-if="interaction.video">
            <video
              class="video-item"
              :src="interaction.video"
              :ref="`video${interaction.id}`"
              controls="controls"
              preload="meta"
              @play="mediaStarted(interaction.id)"
              @ended="mediaEnded(interaction.id)"
              @pause="mediaEnded(interaction.id)"
            ></video>
          </div>
          <div class="image-container" v-if="interaction.image">
            <image-grid
              :box-width="'370'"
              :box-height="'370px'"
              :images="[interaction.image]"
              @imageSelected="showGallery"
            ></image-grid>
          </div>
          <div class="message-container" v-if="interaction.document">
            <div v-if="interaction.document.endsWith('.pdf')">
              <a :href="interaction.document" :ref="`document${interaction.id}`">
                <i class="fa fa-file-pdf-o"></i>
                {{`document_${interaction.document.slice(-20)}`}}
              </a>
            </div>
            <div v-else>
              <a :href="interaction.document" :ref="`document${interaction.id}`">
                <i v-if="interaction.document.endsWith('.doc')
                  || interaction.document.endsWith('.docx')" class="fa fa-file-word-o"></i>
                <i v-else-if="interaction.document.endsWith('.xls')
                  || interaction.document.endsWith('.xlsx')" class="fa fa-file-excel-o"></i>
                {{`document_${interaction.document.slice(-20)}`}}
              </a>
            </div>
          </div>
          <div class="message-container" v-if="interaction.text">
            <a :href="interaction.text" :ref="`document${interaction.id}`">
              <i class="fa fa-file-text"></i> {{ getDocumentName(interaction.text) }}
            </a>
          </div>
          <div v-if="interaction.message" v-html="formatMessage(interaction.message)"></div>
          <div class="datetime">{{ displayDatetime(interaction.datetime) }}</div>
        </div>
        <div class="clearfix"></div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer" v-if="!loadingThreads && replyExpired">
      <div class="text-muted small center">
        24 hour WhatsApp reply period has expired. Due to WhatsApp restrictions replies to this
        number will not be possible until this number first sends a message to us again.
      </div>
    </div>
    <div slot="footer" class="dialog-footer" v-if="!loadingThreads && showReplyOption">
      <div class="upload-media" v-if="showMediaOption">
        <hr v-show="addMedia" />

        <div class="preview" v-if="mediaList.length > 0">
          <div v-for="(preview, index) in mediaPreview" :key="index">
            <div class="audio-preview" v-if="preview.filetype.startsWith('audio')">
              <audio :src="preview.url" controls="controls" preload="none"></audio>
            </div>
            <div class="video-preview" v-if="preview.filetype.startsWith('video')">
              <video
                class="video-item"
                :src="preview.url"
                controls="controls"
                preload="meta"
              ></video>
            </div>
            <div class="image-preview" v-if="preview.filetype.startsWith('image')">
              <image-grid
                :box-width="'370'"
                :box-height="'370px'"
                :images="[preview.url]"
                @imageSelected="showGallery"
              ></image-grid>
            </div>
            <div v-if="preview.filetype === 'application/pdf'">
              <pre> <i class="fa fa-file-pdf-o"></i> {{ preview.filename }} </pre>
            </div>
            <div v-if="preview.filetype.startsWith('text')">
              <pre> <i class="fa fa-file-text"></i> {{ preview.filename }} </pre>
            </div>
            <div v-if="preview.filetype ===
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document'">
              <pre> <i class="fa fa-file-word-o"></i> {{ preview.filename }} </pre>
            </div>
            <div v-if="preview.filetype === 'application/vnd.ms-excel' ||
              preview.filetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'">
              <pre> <i class="fa fa-file-excel-o"></i> {{ preview.filename }} </pre>
            </div>
          </div>
          <div class="preview-control">
            <el-button type="danger" plain @click="handleClearMedia">Clear</el-button>
          </div>
        </div>

        <el-upload
          v-show="addMedia && mediaList.length === 0"
          action="https://jsonplaceholder.typicode.com/posts/"
          accept="image/jpeg, image/png, video/mp4,
            audio/aac, audio/m4a, audio/amr, audio/mpeg, audio/ogg,
            application/pdf,
            application/vnd.ms-excel,
            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
            text/csv,
            text/plain"
          class="post-file-upload"
          :limit="1"
          :list-type="'text'"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :file-list="fileList"
          :auto-upload="false"
          drag
        >
          <i class="el-icon-upload"></i>
          <div class="" style="width: 100%">
            Drop file here or
            <em>click to upload</em>
          </div>
          <div class="el-upload__tip" slot="tip">
            jpg / png images, mp4 video, mp3 / ogg audio, csv / plain text, xlsx / docx / pdf applications
          </div>
        </el-upload>
      </div>

      <el-input v-model="reply" @keyup.enter.native="sendReply">
        <el-button
          slot="prepend"
          icon="fa fa-paperclip"
          v-if="showMediaOption"
          @click="toggleUpload"
        />
        <EmojiKeyboard slot="suffix" class="emoji-keyboard" @onEmojiInsert="handleInsertEmoji" />
        <el-button slot="append" icon="fa fa-arrow-up" @click="sendReply" />
      </el-input>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { take } from 'pipe-operator';

import {
  getOreValue,
  nl2br,
  makeHyperlinks,
  makeHashtags,
  makeMentions,
} from '@/helpers/interaction';
import { getBase64FileObj } from '@/helpers/file';
import Loader from '@/components/common/Loader';
import ImageGrid from '@/components/common/ImageGrid';
import EmojiKeyboard from '../common/EmojiKeyboard';

import * as types from '@/store/modules/interaction/mutation-types';

export default {
  name: 'ThreadDialog',
  data: () => ({
    reply: '',
    addMedia: false,
    fileList: [],
    mediaList: [],
    hasScrolled: false,
    lastScrolledThreadsCount: 0,
    showReplyOption: true,
    replyExpired: false,
  }),
  components: {
    ImageGrid,
    Loader,
    EmojiKeyboard,
  },
  computed: {
    ...mapState('interaction', [
      'threadId',
      'threadChannel',
      'threads',
      'threadDialogVisible',
      'loadingThreads',
    ]),

    dialogVisible: {
      get() {
        return this.threadDialogVisible;
      },
      set(isVisible) {
        this.$store.commit(`interaction/${types.SET_THREAD_DIALOG_VISIBLE}`, isVisible);
      },
    },

    mediaIsPlaying: {
      get() {
        return this.$store.state.interaction.mediaIsPlaying;
      },
      set(mediaIsPlaying) {
        this.$store.commit(`interaction/${types.SET_MEDIA_IS_PLAYING}`, mediaIsPlaying);
      },
    },

    mediaPreview() {
      return this.mediaList.map(item => {
        const fileName = item.filename;
        const fileType = item.filetype;
        const fileB64 = item.base64;
        const fileUrl = `data:${fileType};base64, ${fileB64}`;
        return {
          url: fileUrl,
          filetype: fileType,
          filename: fileName,
        };
      });
    },

    showMediaOption() {
      if (!this.latestInteraction) {
        return false;
      }

      const show = ['whatsapp'];
      const backend = this.latestInteraction.ore.backend[0];
      const isCM = Object.prototype.hasOwnProperty.call(backend, 'additional');
      return show.includes(this.threadChannel) && isCM;
    },

    latestInteraction() {
      return this.threads[0];
    },

    originator() {
      if (this.threads.length > 0) {
        const originator = getOreValue(this.latestInteraction, 'originators');
        const displayName = getOreValue(this.latestInteraction, 'display_name', [null])[0];
        return displayName || originator;
      }
      return '';
    },

    processedThreads() {
      return this.threads
        .reduce((acc, interaction) => {
          const interactionData = {
            id: interaction.id,
            message: interaction.ore.message[0].value,
            // Old api returns as date_created, new graphql comes through as dateCreated
            // TODO: remove once old interaction api is deprecated
            datetime: new Date(interaction.dateCreated || interaction.date_created),
            audio: null,
            video: null,
            image: null,
            direction: 'in',
          };

          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'video')) {
            interactionData.video = interaction.ore.video[0].value;
          }

          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'audio')) {
            interactionData.audio = interaction.ore.audio[0].value;
          }

          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'image')) {
            interactionData.image = interaction.ore.image[0].value;
          }

          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'application')) {
            interactionData.document = interaction.ore.application[0].value;
          }

          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'text')) {
            interactionData.text = interaction.ore.text[0].value;
          }

          let repliesData = [];
          if (Object.prototype.hasOwnProperty.call(interaction.ore, 'reply')) {
            repliesData = interaction.ore.reply.map((reply, index) => {
              let mediaUrl = null;
              let mediaType = null;

              if (Object.prototype.hasOwnProperty.call(reply, 'media') && reply.media) {
                mediaType = ['image', 'audio', 'video', 'application', 'text'].reduce((a, t) => {
                  if (reply.media.filetype.startsWith(t)) {
                    return t;
                  }
                  return a;
                }, null);
                mediaUrl = reply.media.url;
              }

              return {
                id: `r_${interaction.id}_${index}`,
                message: reply.value,
                audio: mediaType === 'audio' ? mediaUrl : null,
                video: mediaType === 'video' ? mediaUrl : null,
                image: mediaType === 'image' ? mediaUrl : null,
                document: mediaType === 'application' ? mediaUrl : null,
                text: mediaType === 'text' ? mediaUrl : null,
                datetime: new Date(this.handleDateCreated(reply)),
                direction: 'out',
              };
            });
          }

          return [...acc, interactionData, ...repliesData];
        }, [])
        .sort((a, b) => a.datetime - b.datetime);
    },
  },
  mounted() {
    this.scrollToBottom();
  },
  methods: {
    formatMessage(message) {
      return take(message)
        .pipe(sanitizeHtml)
        .pipe(nl2br)
        .pipe(makeHyperlinks)
        .pipe(makeHashtags)
        .pipe(makeMentions)
        .result();
    },
    getDocumentName(filename) {
      return filename.endsWith('.bat')
        ? `document_${filename.slice(-20, -4)}.txt`
        : `document_${filename.slice(-20)}`;
    },

    displayDatetime(datetime) {
      return moment(datetime).fromNow();
    },

    scrollToBottom() {
      const container = this.$el.querySelector('#threadContainer');
      if (!container) {
        return;
      }

      // Check that we have threads, on initial load we might have 0, in that case we wait.
      const hasThreads = this.threads.length > 0;
      const hasNewThreads = this.threads.length !== this.lastScrolledThreadCount;

      if (hasThreads && (!this.hasScrolled || hasNewThreads)) {
        container.scrollTop = container.scrollHeight;
        this.hasScrolled = true;
        this.lastScrolledThreadCount = this.threads.length;
      }
    },

    showGallery(args) {
      const [images, imageIndex] = args;
      this.$store.commit(`interaction/${types.SET_GALLERY_IMAGES}`, images);
      this.$store.commit(`interaction/${types.SET_GALLERY_INDEX}`, imageIndex);
      this.$store.commit(`interaction/${types.SET_GALLERY_VISIBLE}`, true);
    },

    sendReply() {
      let media = null;
      if (this.mediaList.length) {
        const fileType = this.mediaList[0].filetype;
        const fileB64 = this.mediaList[0].base64;
        const fileUrl = `data:${fileType};base64, ${fileB64}`;
        media = {
          url: fileUrl,
          filetype: fileType,
        };
      }
      this.$store.commit(`interaction/${types.APPEND_THREAD_REPLY}`, {
        reply: { value: this.reply, date_created: new Date(), media },
        interactionId: this.latestInteraction.id,
      });
      this.$store
        .dispatch('interaction/sendReply', {
          interactionId: this.latestInteraction.id,
          message: this.reply,
          media: this.mediaList,
        })
        .then(() => {
          this.hasScrolled = false;
          setTimeout(() => this.scrollToBottom(), 100);
        });
      this.reply = '';
      this.mediaList = [];
      this.fileList = [];
      this.addMedia = false;
    },

    validateFileSize(file) {
      const bytes = 1024;
      const fileSize = file.size / bytes / bytes; // in MB
      let isValidFileSize = true;
      if (!file.name.match(/.(jpg|jpeg|png|mp4|mp3|ogg|oga|csv|pdf|xls|xlsx|txt|doc|docx)$/i)) {
        this.$alert(
          `Not a supported file type, only jpg / png images, mp4 video, mp3 / ogg audio,
          csv / plain text, xlsx / docx / pdf applications are accepted`,
        );
        isValidFileSize = false;
      }
      if (fileSize > 10) {
        this.$alert('File size exceeds 10MB maximum limit');
        isValidFileSize = false;
      }

      return file && isValidFileSize;
    },

    toggleUpload() {
      this.addMedia = !this.addMedia;
    },

    mediaStarted(interactionId) {
      this.mediaIsPlaying = this.mediaIsPlaying.concat(interactionId);
    },

    mediaEnded(interactionId) {
      this.mediaIsPlaying = this.mediaIsPlaying.filter(id => interactionId !== id);
    },

    pauseAllMedia() {
      Object.keys(this.$refs)
        .filter(refKey => refKey.startsWith('audio') || refKey.startsWith('video'))
        .forEach(mediaKey => {
          const mediaElem = this.$refs[mediaKey][0];
          if (mediaElem) {
            mediaElem.pause();
          }
        });
    },

    handleClearMedia() {
      this.mediaList = [];
      this.fileList = [];
    },

    handleFileChange(file) {
      if (this.validateFileSize(file)) {
        this.fileList = [file]; // replace previous files.

        getBase64FileObj(file.raw)
          .then(fileObj => {
            this.mediaList = [fileObj];
          })
          .catch(() => {
            this.$alert('Unable to read file', { type: 'error' });
            this.handleClearMedia();
          });
      } else {
        this.handleClearMedia();
      }
    },

    handleFileRemove() {
      this.fileList = [];
      this.mediaList = [];
    },

    shouldShowReplyOption() {
      if (this.latestInteraction) {
        const expireChannels = ['whatsapp'];
        const hours = moment().diff(moment(this.latestInteraction.date_created || this.latestInteraction.dateCreated), 'hours');
        this.replyExpired = expireChannels.includes(this.threadChannel) && hours >= 24;

        const doNotShow = ['instagram'];
        this.showReplyOption = !doNotShow.includes(this.threadChannel) && !this.replyExpired;

        const noReplyChannels = ['email', 'webform'];
        if (noReplyChannels.includes(this.threadChannel)) {
          this.showReplyOption = false;
        }
      }
    },

    handleInsertEmoji(emoji) {
      this.reply += emoji;
    },

    handleDateCreated(reply) {
      if (!reply.date_created) {
        const timestamp = reply.date;
        return timestamp.$date;
      }
      return reply.date_created;
    },
  },
  watch: {
    threads() {
      setTimeout(() => this.scrollToBottom(), 0);
      this.shouldShowReplyOption();
    },

    threadDialogVisible() {
      // Ensure reply content is cleared when visibility changed
      this.reply = '';
      this.hasScrolled = false;
      this.fileList = [];
      this.mediaList = [];

      if (this.threadDialogVisible) {
        setTimeout(() => this.scrollToBottom(), 0);
      } else {
        this.pauseAllMedia();
      }
    },

    mediaIsPlaying() {
      const lastPlaying = this.mediaIsPlaying[this.mediaIsPlaying.length - 1];

      Object.keys(this.$refs)
        .filter(
          refKey =>
            (refKey.startsWith('audio') || refKey.startsWith('video')) &&
            !refKey.includes(lastPlaying),
        )
        .forEach(mediaKey => {
          const mediaElem = this.$refs[mediaKey][0];
          if (mediaElem) {
            mediaElem.pause();
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-footer {
  text-align: left;
}
.thread-container {
  height: calc(60vh);
  overflow-y: scroll;
}
.message {
  padding: 1rem 1.2rem;
  margin: 1rem 0;
  border-radius: 6px;
  display: inline-block;

  &.in {
    float: left;
    text-align: left;
    background-color: rgb(229, 231, 235);
  }

  &.out {
    float: right;
    text-align: right;
    background-color: rgb(191, 219, 254);
  }

  .datetime {
    color: rgb(55, 65, 81);
    font-size: 0.93rem;
    text-align: right;
  }

  .audio-container {
    text-align: center;
  }
  .video-container {
    text-align: center;
  }
  .image-container {
    height: 370px;

    img {
      border: 1px solid #eee;
      width: 100%;
    }
  }
  .video-item {
    height: 370px !important;
  }
  .audio-container,
  .image-container,
  .video-container {
    margin-top: 8px;
  }
}
.upload-media {
  margin-bottom: 12px;
}
.preview {
  .image-preview {
    img {
      border-radius: 6px;
    }
  }
  .preview-controls {
    text-align: right;
  }
}
</style>



// WEBPACK FOOTER //
// ThreadDialog.vue?0cf4873d